<template>
  <div id="app" class="flex flex-col min-h-screen">
    <!-- Navbar -->
    <header class="bg-white shadow-sm py-3 sticky top-0 z-50">
      <div class="container mx-auto px-4">
        <nav class="flex items-center justify-between">
          <!-- Logo -->
          <router-link to="/" class="flex-shrink-0">
            <span class="text-primary font-bold">FULL</span><span class="text-secondary">CHOCADOS</span>
          </router-link>
          
          <!-- Mobile menu button -->
          <div class="md:hidden">
            <button @click="mobileMenuOpen = !mobileMenuOpen" type="button" class="text-gray-600 hover:text-primary focus:outline-none">
              <svg v-if="!mobileMenuOpen" xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16" />
              </svg>
              <svg v-else xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          <!-- Desktop Navigation -->
          <div class="hidden md:flex md:items-center md:justify-between md:flex-1 ml-6">
            <div class="flex space-x-4">
              <!-- Mostrar Vehículos solo a compradores -->
              <router-link v-if="isAuthenticated && isBuyer && !READ_ONLY_LANDING" to="/vehicles" class="px-3 py-2 text-gray-700 hover:text-primary transition-colors duration-200">Vehículos</router-link>
              <router-link to="/about" class="px-3 py-2 text-gray-700 hover:text-primary transition-colors duration-200">Acerca de</router-link>
              <router-link to="/faqs" class="px-3 py-2 text-gray-700 hover:text-primary transition-colors duration-200">FAQ</router-link>
            </div>
            
            <div class="flex items-center space-x-4">
              <!-- Authenticated User -->
              <template v-if="isAuthenticated">
                <!-- Notifications Dropdown -->
                <div class="relative" v-click-outside="closeNotifications">
                  <button @click="notificationsOpen = !notificationsOpen" class="relative p-2 text-gray-600 hover:text-primary focus:outline-none">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                    </svg>
                    <span v-if="unreadNotifications > 0" class="absolute top-0 right-0 inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-white transform translate-x-1/2 -translate-y-1/2 bg-red-500 rounded-full">{{ unreadNotifications }}</span>
                  </button>
                  
                  <div v-show="notificationsOpen" class="absolute right-0 mt-2 w-80 bg-white rounded-md shadow-lg py-1 z-50">
                    <div v-if="notifications.length === 0" class="px-4 py-3 text-sm text-gray-500">No hay notificaciones</div>
                    <template v-else>
                      <div v-for="notification in notifications.slice(0, 5)" :key="notification.id" 
                           @click="markAsRead(notification)"
                           class="px-4 py-3 hover:bg-gray-100 cursor-pointer">
                        <p :class="{'font-bold': notification.status === 'pending', 'text-sm': true}">{{ notification.message }}</p>
                      </div>
                      <div class="border-t border-gray-100"></div>
                      <router-link to="/notifications" class="block px-4 py-2 text-sm text-primary hover:bg-gray-100">Ver todas</router-link>
                    </template>
                  </div>
                </div>
                
                <!-- User Profile Dropdown -->
                <div class="relative" v-click-outside="closeUserMenu">
                  <button @click="userMenuOpen = !userMenuOpen" class="flex items-center space-x-2 focus:outline-none">
                    <div class="w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center text-sm font-medium">{{ getUserInitials() }}</div>
                    <span class="hidden md:block text-sm font-medium text-gray-700">{{ currentUser.first_name + ' ' + currentUser.last_name || 'Usuario' }}</span>
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd" />
                    </svg>
                  </button>
                  
                  <div v-show="userMenuOpen" class="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-50">

                    <router-link to="/dashboard" @click.native="handleLinkClick" class="flex px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-2 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                      </svg>
                      Dashboard
                    </router-link>
                    
                    <!-- Admin Panel Link (Solo para staff) -->
                    <router-link v-if="currentUser && (currentUser.is_staff || currentUser.is_superuser)" to="/staff" @click.native="handleLinkClick" class="flex px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-2 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                      </svg>
                      <span class="font-medium">Panel de Administración</span>
                    </router-link>
                    <!-- Mostrar vehículos solo a vendedores -->
                    <router-link v-if="isSeller" to="/my-vehicles" @click.native="handleLinkClick" class="flex px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-2 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      Mis Vehículos
                    </router-link>
                    <!-- Mostrar ofertas para todos los usuarios autenticados -->
                    <router-link to="/my-offers" @click.native="handleLinkClick" class="flex px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-2 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      Mis Ofertas
                    </router-link>
                    <!-- Mostrar señas solo a compradores -->
                    <router-link v-if="isBuyer" to="/my-deposits" @click.native="handleLinkClick" class="flex px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-2 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                      </svg>
                      Mis Señas
                    </router-link>
                    <div class="border-t border-gray-100 my-1"></div>
                    <button @click="logout" class="flex w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-100">
                      <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                      </svg>
                      Cerrar Sesión
                    </button>
                  </div>
                </div>
              </template>
              
              <!-- Guest User -->
              <template v-else>
                <router-link v-if="!READ_ONLY_LANDING" to="/login" class="px-4 py-2 border border-primary text-primary hover:bg-primary hover:text-white transition duration-200 rounded-full">Iniciar Sesión</router-link>
                <router-link v-if="!READ_ONLY_LANDING" to="/register" class="px-4 py-2 bg-primary hover:bg-primary-600 text-white transition duration-200 rounded-full">Registrarse</router-link>
              </template>
            </div>
          </div>
        </nav>
        
        <!-- Mobile menu -->
        <div v-show="mobileMenuOpen" class="md:hidden">
          <div class="pt-2 pb-4 space-y-1">
            <!-- Mostrar Vehículos solo a compradores -->
            <router-link v-if="isAuthenticated && isBuyer && !READ_ONLY_LANDING" to="/vehicles" @click.native="handleMobileLinkClick" class="block px-3 py-2 text-base font-medium text-gray-700 hover:text-primary hover:bg-gray-50 rounded-md">Vehículos</router-link>
            <router-link to="/about" @click.native="handleMobileLinkClick" class="block px-3 py-2 text-base font-medium text-gray-700 hover:text-primary hover:bg-gray-50 rounded-md">Acerca de</router-link>
            <router-link to="/faqs" @click.native="handleMobileLinkClick" class="block px-3 py-2 text-base font-medium text-gray-700 hover:text-primary hover:bg-gray-50 rounded-md">FAQ</router-link>
            
            <template v-if="isAuthenticated">
              <div class="border-t border-gray-200 my-2"></div>
              <router-link to="/dashboard" @click.native="handleMobileLinkClick" class="block px-3 py-2 text-base font-medium text-gray-700 hover:text-primary hover:bg-gray-50 rounded-md">Dashboard</router-link>
              <!-- Mostrar vehículos solo a vendedores -->
              <router-link v-if="isSeller" to="/my-vehicles" @click.native="handleMobileLinkClick" class="block px-3 py-2 text-base font-medium text-gray-700 hover:text-primary hover:bg-gray-50 rounded-md">Mis Vehículos</router-link>
              <!-- Mostrar ofertas para todos los usuarios autenticados -->
              <router-link to="/my-offers" @click.native="handleMobileLinkClick" class="block px-3 py-2 text-base font-medium text-gray-700 hover:text-primary hover:bg-gray-50 rounded-md">Mis Ofertas</router-link>
              <!-- Mostrar señas solo a compradores -->
              <router-link v-if="isBuyer" to="/my-deposits" @click.native="handleMobileLinkClick" class="block px-3 py-2 text-base font-medium text-gray-700 hover:text-primary hover:bg-gray-50 rounded-md">Mis Señas</router-link>
              <router-link to="/notifications" @click.native="handleMobileLinkClick" class="block px-3 py-2 text-base font-medium text-gray-700 hover:text-primary hover:bg-gray-50 rounded-md">
                Notificaciones
                <span v-if="unreadNotifications > 0" class="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">{{ unreadNotifications }}</span>
              </router-link>
              <button @click="handleLogoutClick" class="w-full text-left px-3 py-2 text-base font-medium text-red-600 hover:text-red-800 hover:bg-gray-50 rounded-md">Cerrar Sesión</button>
            </template>
            <template v-else>
              <div class="border-t border-gray-200 my-2"></div>
              <router-link v-if="!READ_ONLY_LANDING" to="/login" @click.native="handleMobileLinkClick" class="block px-3 py-2 text-base font-medium text-primary hover:bg-gray-50 rounded-md">Iniciar Sesión</router-link>
              <router-link v-if="!READ_ONLY_LANDING" to="/register" @click.native="handleMobileLinkClick" class="block px-3 py-2 text-base font-medium text-primary hover:bg-gray-50 rounded-md">Registrarse</router-link>
            </template>
          </div>
        </div>
      </div>
    </header>

    <main class="flex-grow">
      <router-view />
    </main>

    <!-- Botón flotante de WhatsApp para ayuda -->
    <WhatsAppHelp v-if="!isStaffRoute" />

    <footer v-if="!isStaffRoute" class="bg-gray-800 py-12 mt-10">
      <div class="container mx-auto px-4">
        <div class="grid grid-cols-1 md:grid-cols-12 gap-8">
          <div class="md:col-span-5">
            <div class="mb-6">
              <h3 class="text-2xl font-bold mb-4">
                <span class="text-white">FULL</span><span class="text-secondary">CHOCADOS</span>
              </h3>
              <p class="text-gray-300">La plataforma moderna para compra y venta de vehículos con daños.</p>
            </div>
            <div class="flex space-x-4">
              <a href="https://www.facebook.com/people/Full-Chocados/100069389234782" target="_blank" rel="noopener noreferrer" class="w-10 h-10 rounded-full bg-gray-700 flex items-center justify-center text-gray-300 hover:bg-primary hover:text-white transition duration-200">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z"/>
                </svg>
              </a>
              <a href="https://instagram.com/Full.chocados" target="_blank" rel="noopener noreferrer" class="w-10 h-10 rounded-full bg-gray-700 flex items-center justify-center text-gray-300 hover:bg-primary hover:text-white transition duration-200">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12.315 2c2.43 0 2.784.013 3.808.06 1.064.049 1.791.218 2.427.465a4.902 4.902 0 011.772 1.153 4.902 4.902 0 011.153 1.772c.247.636.416 1.363.465 2.427.048 1.067.06 1.407.06 4.123v.08c0 2.643-.012 2.987-.06 4.043-.049 1.064-.218 1.791-.465 2.427a4.902 4.902 0 01-1.153 1.772 4.902 4.902 0 01-1.772 1.153c-.636.247-1.363.416-2.427.465-1.067.048-1.407.06-4.123.06h-.08c-2.643 0-2.987-.012-4.043-.06-1.064-.049-1.791-.218-2.427-.465a4.902 4.902 0 01-1.772-1.153 4.902 4.902 0 01-1.153-1.772c-.247-.636-.416-1.363-.465-2.427-.047-1.024-.06-1.379-.06-3.808v-.63c0-2.43.013-2.784.06-3.808.049-1.064.218-1.791.465-2.427a4.902 4.902 0 011.153-1.772A4.902 4.902 0 015.45 2.525c.636-.247 1.363-.416 2.427-.465C8.901 2.013 9.256 2 11.685 2h.63zm-.081 1.802h-.468c-2.456 0-2.784.011-3.807.058-.975.045-1.504.207-1.857.344-.467.182-.8.398-1.15.748-.35.35-.566.683-.748 1.15-.137.353-.3.882-.344 1.857-.047 1.023-.058 1.351-.058 3.807v.468c0 2.456.011 2.784.058 3.807.045.975.207 1.504.344 1.857.182.466.399.8.748 1.15.35.35.683.566 1.15.748.353.137.882.3 1.857.344 1.054.048 1.37.058 4.041.058h.08c2.597 0 2.917-.01 3.96-.058.976-.045 1.505-.207 1.858-.344.466-.182.8-.398 1.15-.748.35-.35.566-.683.748-1.15.137-.353.3-.882.344-1.857.048-1.055.058-1.37.058-4.041v-.08c0-2.597-.01-2.917-.058-3.96-.045-.976-.207-1.505-.344-1.858a3.097 3.097 0 00-.748-1.15 3.098 3.098 0 00-1.15-.748c-.353-.137-.882-.3-1.857-.344-1.023-.047-1.351-.058-3.807-.058zM12 6.865a5.135 5.135 0 110 10.27 5.135 5.135 0 010-10.27zm0 1.802a3.333 3.333 0 100 6.666 3.333 3.333 0 000-6.666zm5.338-3.205a1.2 1.2 0 110 2.4 1.2 1.2 0 010-2.4z"/>
                </svg>
              </a>
            </div>
          </div>
          
          <div class="md:col-span-3 sm:col-span-6">
            <h5 class="text-lg font-bold text-white mb-4">Enlaces</h5>
            <ul class="space-y-2">
              <li><router-link to="/" class="text-gray-300 hover:text-white transition duration-200">Inicio</router-link></li>
              <li v-if="isAuthenticated && !READ_ONLY_LANDING"><router-link to="/vehicles" class="text-gray-300 hover:text-white transition duration-200">Vehículos</router-link></li>
              <li><router-link to="/about" class="text-gray-300 hover:text-white transition duration-200">Sobre Nosotros</router-link></li>
              <li><router-link to="/faqs" class="text-gray-300 hover:text-white transition duration-200">Preguntas Frecuentes</router-link></li>
              <li><router-link to="/terms" class="text-gray-300 hover:text-white transition duration-200">Términos y Condiciones</router-link></li>
              <li><router-link to="/privacy" class="text-gray-300 hover:text-white transition duration-200">Política de Privacidad</router-link></li>
            </ul>
          </div>
          
          <div class="md:col-span-4 sm:col-span-6">
            <h5 class="text-lg font-bold text-white mb-4">Contacto</h5>
            <ul class="space-y-3">
              <li class="flex items-start">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-secondary mr-2 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <a href="mailto:info@fullchocados.com" class="text-gray-300 hover:text-white transition duration-200">info@fullchocados.com</a>
              </li>
              <li class="flex items-start">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-secondary mr-2 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
                <a href="https://wa.me/+5492613426644" class="text-gray-300 hover:text-white transition duration-200" target="_blank" rel="noopener">WhatsApp: +54 9 261 342-6644</a>
              </li>
              <li class="flex items-start">
                <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-secondary mr-2 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <span class="text-gray-300">Confraternidad Ferroviaria 1140, Ciudad de Mendoza.</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
      
      <div class="border-t border-gray-700 mt-10 pt-6">
        <div class="container mx-auto px-4">
          <div class="flex flex-col md:flex-row justify-between items-center">
            <p class="text-gray-400 mb-4 md:mb-0">&copy; {{ new Date().getFullYear() }} FullChocados. Todos los derechos reservados.</p>
            <div class="flex space-x-6">
              <router-link to="/privacy" class="text-gray-400 hover:text-white transition duration-200">Privacidad</router-link>
              <router-link to="/terms" class="text-gray-400 hover:text-white transition duration-200">Términos</router-link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import { READ_ONLY_LANDING } from './featureFlags'
import WhatsAppHelp from './components/WhatsAppHelp.vue'

export default {
  name: 'App',
  components: {
    WhatsAppHelp
  },
  data() {
    return {
      mobileMenuOpen: false,
      notificationsOpen: false,
      userMenuOpen: false
    }
  },
  computed: {
    ...mapState([
      'notifications'
    ]),
    ...mapGetters([
      'isAuthenticated',
      'currentUser',
      'isSeller',
      'isBuyer',
      'isStaff'
    ]),
    READ_ONLY_LANDING() {
      return READ_ONLY_LANDING
    },
    isStaffRoute() {
      return this.$route.matched.some(record => record.meta && record.meta.requiresStaff)
    },
    unreadNotifications() {
      return this.notifications.filter(n => n.status === 'pending').length
    }
  },
  methods: {
    ...mapActions([
      'logout',
      'markNotificationAsRead'
    ]),
    markAsRead(notification) {
      this.markNotificationAsRead(notification.id)
    },
    getUserInitials() {
      try {
        if (!this.currentUser) return '?';
        
        const firstName = this.currentUser?.first_name || '';
        const lastName = this.currentUser?.last_name || '';
        
        if (firstName && lastName) {
          return `${firstName[0]}${lastName[0]}`.toUpperCase();
        } else if (firstName) {
          return firstName[0].toUpperCase();
        } else if (this.currentUser?.email) {
          return this.currentUser.email[0].toUpperCase();
        }
      } catch (error) {
        console.error('Error getting user initials:', error);
      }
      
      return '?';
    },
    closeNotifications() {
      this.notificationsOpen = false;
    },
    closeUserMenu() {
      this.userMenuOpen = false;
    },
    handleLinkClick() {
      this.userMenuOpen = false;
      this.mobileMenuOpen = false;
    },
    handleMobileLinkClick() {
      // Cerrar el menú móvil cuando se hace clic en un enlace
      this.mobileMenuOpen = false;
    },
    handleLogoutClick() {
      // Cerrar el menú móvil y ejecutar logout
      this.mobileMenuOpen = false;
      this.logout();
    }
  },
  created() {
    // Asegurarse de que el perfil del usuario esté cargado al iniciar la app
    if (this.isAuthenticated) {
      if (!this.currentUser || this.currentUser.is_staff === undefined) {
        this.$store.dispatch('fetchUserProfile');
      }
      
      // Cargar depósitos y ofertas del usuario al iniciar la aplicación
      this.$store.dispatch('fetchUserDeposits');
      this.$store.dispatch('fetchUserOffers');
    }
  },
  watch: {
    // Observar cambios en el estado de autenticación
    isAuthenticated(newValue) {
      if (newValue) {
        // Cuando el usuario inicia sesión, cargar sus datos
        this.$store.dispatch('fetchUserProfile');
        this.$store.dispatch('fetchUserDeposits');
        this.$store.dispatch('fetchUserOffers');
      }
    },
    // Cerrar el menú móvil cuando cambie la ruta
    '$route'() {
      this.mobileMenuOpen = false;
      this.userMenuOpen = false;
      this.notificationsOpen = false;
    }
  },
  directives: {
    clickOutside: {
      mounted(el, binding) {
        el.clickOutsideEvent = function(event) {
          if (!(el === event.target || el.contains(event.target))) {
            binding.value(event);
          }
        };
        document.addEventListener('click', el.clickOutsideEvent);
      },
      unmounted(el) {
        document.removeEventListener('click', el.clickOutsideEvent);
      }
    }
  }
}
</script>

<style>
@import url('https://fonts.googleapis.com/css2?family=Nunito:wght@400;500;600;700;800&display=swap');

/* Estilos globales que no pueden ser manejados por Tailwind */
:root {
  --color-primary: #FF6B00;
  --color-primary-light: #FF9A4D;
  --color-primary-dark: #CC5500;
}

/* Animación para el menú desplegable */
@keyframes fadeIn {
  from { opacity: 0; transform: translateY(-10px); }
  to { opacity: 1; transform: translateY(0); }
}

.animate-fadeIn {
  animation: fadeIn 0.2s ease-out forwards;
}

/* Animación para el hover de los enlaces */
.hover-underline-animation {
  position: relative;
}

.hover-underline-animation::after {
  content: '';
  position: absolute;
  width: 0;
  height: 2px;
  bottom: 0;
  left: 0;
  background-color: var(--color-primary);
  transition: width 0.3s ease;
}

.hover-underline-animation:hover::after {
  width: 100%;
}

/* Estilo para los enlaces activos en el navbar */
.router-link-active.nav-link {
  color: var(--color-primary);
}

/* Estilos para el scrollbar personalizado */
::-webkit-scrollbar {
  width: 8px;
}

::-webkit-scrollbar-track {
  background: #f1f1f1;
}

::-webkit-scrollbar-thumb {
  background: #ccc;
  border-radius: 4px;
}

::-webkit-scrollbar-thumb:hover {
  background: #aaa;
}

/* Estilos modernos para la aplicación */

/* Navbar */
.navbar-modern {
  background-color: #fff !important;
}

.brand-logo {
  font-size: 1.4rem;
  letter-spacing: -0.5px;
  font-weight: 700;
}

.nav-link-modern {
  font-weight: 600;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 50%;
    width: 0;
    height: 2px;
    background-color: #FF6B00;
    transition: all 0.3s ease;
    transform: translateX(-50%);
  }
  
  &:hover::after, &.router-link-active::after {
    width: 80%;
  }
}

.notification-icon {
  font-size: 1.2rem;
  position: relative;
}

.notification-badge {
  position: absolute;
  top: -5px;
  right: -5px;
  font-size: 0.7rem;
}

.user-avatar {
  width: 32px;
  height: 32px;
  background-color: #FF6B00;
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 0.9rem;
}

.dropdown-item-modern {
  font-weight: 500;
  padding: 0.75rem 1.5rem;
}

/* Footer */
.footer-modern {
  background-color: #333333;
}

.footer-heading {
  font-weight: 600;
  position: relative;
  display: inline-block;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 0;
    width: 50px;
    height: 2px;
    background-color: #FF6B00;
  }
}

.footer-links {
  margin-top: 25px;
  
  li {
    margin-bottom: 10px;
  }
}

.footer-link {
  color: #ccc;
  transition: all 0.3s;
  text-decoration: none;
  
  &:hover {
    color: #FF6B00;
    text-decoration: none;
  }
}

.footer-brand {
  font-size: 1.8rem;
  letter-spacing: -0.5px;
}

.social-icons {
  display: flex;
  gap: 15px;
}

.social-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: rgba(255, 255, 255, 0.1);
  color: #fff;
  transition: all 0.3s;
  
  &:hover {
    background-color: #FF6B00;
    color: white;
    text-decoration: none;
  }
}

.footer-bottom {
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  padding-top: 20px;
}

/* Botones */
.btn-primary {
  font-weight: 600;
  box-shadow: 0 4px 6px rgba(255, 107, 0, 0.2);
  
  &:hover {
    box-shadow: 0 6px 8px rgba(255, 107, 0, 0.3);
    transform: translateY(-2px);
  }
}

/* Estilos globales */
body {
  font-family: 'Nunito', sans-serif;
  color: #333;
}

h1, h2, h3, h4, h5, h6 {
  font-weight: 700;
}

.card {
  border: none;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  
  &:hover {
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.08);
  }
}

.section-heading {
  position: relative;
  display: inline-block;
  font-weight: 700;
  margin-bottom: 1.5rem;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 0;
    width: 80px;
    height: 3px;
    background-color: #FF6B00;
  }
}

/* Animaciones */
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s;
}

.fade-enter, .fade-leave-to {
  opacity: 0;
}
</style>
